import type { UiTheme } from './theme/uiTheme';
import { EXTRA_TEXT_TEMPLATE_CATEGORIES } from './textTemplateCatalogExtra';
import { SALON_SPA_CLINIC_TEXT_TEMPLATE_CATEGORIES } from './textTemplateCatalogSalonSpa';

/** Partial text annotation fields applied on top of the wrapper default text object. */
export type TextTemplateStyle = Record<string, unknown>;

export type TextTemplateItem = {
  id: string;
  /** Shown in the picker tile (may differ from canvas `text`). */
  label: string;
  style: TextTemplateStyle;
  /** Preview font size divisor (canvas size / scale). Default 1.6. */
  previewScale?: number;
  /** Use light/dark canvas fill instead of a fixed color. */
  themeAdaptiveFill?: boolean;
  /**
   * When true, insert a rounded rect behind text (grouped). When false, text only.
   * Default: dark preview card tiles (non theme-adaptive / stroke / glow styles).
   */
  previewCard?: boolean;
  keywords?: string[];
};

/** Same rule as `.respark_text-template-tile--effect` in the picker. */
export const templateUsesPreviewCard = (item: TextTemplateItem): boolean => {
  if (item.previewCard === true) return true;
  if (item.previewCard === false) return false;
  return Boolean(
    (item.style.shadowBlur && Number(item.style.shadowBlur) > 0) ||
      (item.style.strokeWidth && Number(item.style.strokeWidth) > 0) ||
      !item.themeAdaptiveFill,
  );
};

export type TextTemplateCategory = {
  id: string;
  title: string;
  description?: string;
  templates: TextTemplateItem[];
};

const CANVAS_FILL_LIGHT = '#111111';
const CANVAS_FILL_DARK = '#ffffff';

export const resolveCanvasFillForTheme = (
  theme: UiTheme,
  template: Pick<TextTemplateItem, 'themeAdaptiveFill' | 'style'>,
): string | undefined => {
  if (template.themeAdaptiveFill) {
    return theme === 'dark' ? CANVAS_FILL_DARK : CANVAS_FILL_LIGHT;
  }
  const fill = template.style.fill;
  return typeof fill === 'string' ? fill : undefined;
};

export const applyThemeToTemplateStyle = (
  theme: UiTheme,
  template: TextTemplateItem,
): TextTemplateStyle => {
  const fill = resolveCanvasFillForTheme(theme, template);
  if (!fill) return { ...template.style };
  return { ...template.style, fill };
};

/** Style applied on canvas — always uses picker `label` (same string as the tile preview). */
export const getTextTemplateCanvasStyle = (
  theme: UiTheme,
  template: TextTemplateItem,
): TextTemplateStyle => ({
  ...applyThemeToTemplateStyle(theme, template),
  text: template.label,
});

/** Centered banner / marketing preset with shared layout defaults. */
const marketingBanner = (
  id: string,
  label: string,
  fontFamily: string,
  fontSize: number,
  width: number,
  extra: TextTemplateStyle & {
    themeAdaptiveFill?: boolean;
    previewScale?: number;
    keywords?: string[];
  } = {},
): TextTemplateItem => {
  const { themeAdaptiveFill, previewScale, keywords, ...styleExtra } = extra;
  return {
    id,
    label,
    themeAdaptiveFill,
    previewScale: previewScale ?? (fontSize >= 56 ? 2.25 : 2),
    keywords: ['banner', 'marketing', 'branding', label, ...(keywords ?? [])],
    style: {
      text: label,
      fontFamily,
      fontSize,
      width,
      height: Math.round(fontSize * 1.15),
      align: 'center',
      letterSpacing: 0,
      lineHeight: 1.05,
      x: Math.max(50, Math.round((520 - width) / 2)),
      y: 95,
      ...styleExtra,
    },
  };
};

/** Compact corner badge / product label (small type, high contrast). */
const retailTag = (
  id: string,
  label: string,
  fontFamily: string,
  fontSize: number,
  width: number,
  extra: TextTemplateStyle & {
    themeAdaptiveFill?: boolean;
    previewScale?: number;
    keywords?: string[];
  } = {},
): TextTemplateItem =>
  marketingBanner(id, label, fontFamily, fontSize, width, {
    fontStyle: 'bold',
    letterSpacing: fontSize <= 22 ? 2.5 : 1.5,
    previewScale: extra.previewScale ?? (fontSize <= 24 ? 2.65 : 2.35),
    keywords: ['tag', 'label', 'badge', 'retail', ...(extra.keywords ?? [])],
    ...extra,
  });

const quick = (
  id: string,
  label: string,
  fontSize: number,
  width: number,
  extra: TextTemplateStyle = {},
): TextTemplateItem => ({
  id,
  label,
  themeAdaptiveFill: true,
  previewScale: 1.45,
  keywords: [label],
  style: {
    text: label,
    fontSize,
    width,
    height: Math.round(fontSize * 1.15),
    align: 'left',
    fontFamily: 'Poppins',
    letterSpacing: 0,
    lineHeight: 1.1,
    x: 80,
    y: 100,
    ...extra,
  },
});

const CORE_TEXT_TEMPLATE_CATEGORIES: TextTemplateCategory[] = [
  {
    id: 'quick',
    title: 'Quick add',
    description: 'Plain text you can restyle on the canvas',
    templates: [
      quick('heading', 'Add a heading', 48, 400, { fontStyle: 'bold' }),
      quick('subheading', 'Add a subheading', 32, 340),
      quick('body', 'Add a body text', 20, 220),
      quick('caption', 'Caption text', 16, 200, { align: 'center' }),
      quick('label', 'LABEL', 14, 160, {
        fontStyle: 'bold',
        letterSpacing: 2,
        align: 'center',
      }),
      quick('emoji-thanks', 'Thank you ❤️', 32, 300, { align: 'center' }),
      quick('emoji-sparkle', 'Special offer ✨', 28, 320, { align: 'center' }),
      quick('emoji-celebrate', '🎉 Celebrate', 36, 280, { align: 'center', fontStyle: 'bold' }),
    ],
  },
  {
    id: 'bold-display',
    title: 'Bold display',
    description: 'Impact titles and hero lines',
    templates: [
      {
        id: 'bebas-hero',
        label: 'HERO TITLE',
        previewScale: 2.1,
        style: {
          text: 'HERO TITLE',
          fontFamily: 'Bebas Neue',
          fontSize: 72,
          width: 420,
          height: 80,
          align: 'center',
          fontStyle: 'normal',
          letterSpacing: 2,
          x: 60,
          y: 90,
          fill: '#f8558e',
        },
        keywords: ['display', 'poster'],
      },
      {
        id: 'anton-shout',
        label: 'BIG HEADLINE',
        previewScale: 2,
        style: {
          text: 'BIG HEADLINE',
          fontFamily: 'Anton',
          fontSize: 64,
          width: 400,
          height: 72,
          align: 'center',
          fontStyle: 'normal',
          letterSpacing: 1,
          x: 70,
          y: 95,
          fill: '#ffffff',
          stroke: '#111111',
          strokeWidth: 1,
        },
      },
      {
        id: 'archivo-black',
        label: 'STATEMENT',
        previewScale: 2,
        themeAdaptiveFill: true,
        style: {
          text: 'STATEMENT',
          fontFamily: 'Archivo Black',
          fontSize: 56,
          width: 380,
          height: 68,
          align: 'center',
          fontStyle: 'normal',
          letterSpacing: 0,
          x: 75,
          y: 100,
        },
      },
      {
        id: 'abril-fatface',
        label: 'Editorial',
        previewScale: 1.9,
        style: {
          text: 'Editorial',
          fontFamily: 'Abril Fatface',
          fontSize: 52,
          width: 360,
          height: 64,
          align: 'center',
          fontStyle: 'normal',
          x: 90,
          y: 105,
          fill: '#c9a227',
        },
      },
      {
        id: 'oswald-condensed',
        label: 'CONDENSED TITLE',
        previewScale: 2.2,
        themeAdaptiveFill: true,
        style: {
          text: 'CONDENSED TITLE',
          fontFamily: 'Oswald',
          fontSize: 58,
          width: 400,
          height: 70,
          align: 'center',
          letterSpacing: 3,
          x: 65,
          y: 98,
        },
      },
      {
        id: 'lilita-fun',
        label: 'FUN HEADLINE',
        previewScale: 2,
        style: {
          text: 'FUN HEADLINE',
          fontFamily: 'Lilita One',
          fontSize: 54,
          width: 390,
          height: 66,
          align: 'center',
          x: 72,
          y: 102,
          fill: '#ff6b35',
        },
      },
    ],
  },
  {
    id: 'serif-elegant',
    title: 'Elegant serif',
    description: 'Luxury, editorial, and formal layouts',
    templates: [
      {
        id: 'playfair-title',
        label: 'Classic Title',
        previewScale: 1.75,
        themeAdaptiveFill: true,
        style: {
          text: 'Classic Title',
          fontFamily: 'Playfair Display',
          fontSize: 48,
          width: 360,
          height: 58,
          align: 'center',
          fontStyle: 'normal',
          x: 95,
          y: 108,
        },
      },
      {
        id: 'cormorant-quote',
        label: '“Inspiring quote”',
        previewScale: 1.7,
        style: {
          text: 'Inspiring quote',
          fontFamily: 'Cormorant Garamond',
          fontSize: 42,
          width: 340,
          height: 54,
          align: 'center',
          fontStyle: 'italic',
          x: 100,
          y: 112,
          fill: '#2c3e50',
        },
      },
      {
        id: 'dm-serif-brand',
        label: 'Brand Name',
        previewScale: 1.8,
        themeAdaptiveFill: true,
        style: {
          text: 'Brand Name',
          fontFamily: 'DM Serif Display',
          fontSize: 50,
          width: 350,
          height: 60,
          align: 'center',
          x: 92,
          y: 106,
        },
      },
      {
        id: 'lora-subtle',
        label: 'Refined subhead',
        previewScale: 1.55,
        themeAdaptiveFill: true,
        style: {
          text: 'Refined subhead',
          fontFamily: 'Lora',
          fontSize: 32,
          width: 320,
          height: 42,
          align: 'left',
          x: 110,
          y: 120,
        },
      },
      {
        id: 'bodoni-fashion',
        label: 'FASHION',
        previewScale: 2,
        style: {
          text: 'FASHION',
          fontFamily: 'Bodoni Moda',
          fontSize: 56,
          width: 340,
          height: 64,
          align: 'center',
          letterSpacing: 6,
          x: 100,
          y: 100,
          fill: '#111111',
        },
      },
    ],
  },
  {
    id: 'script-handwriting',
    title: 'Script & handwriting',
    description: 'Signatures, invites, and personal touches',
    templates: [
      {
        id: 'dancing-script',
        label: 'Handwritten note',
        previewScale: 1.65,
        style: {
          text: 'Handwritten note',
          fontFamily: 'Dancing Script',
          fontSize: 44,
          width: 320,
          height: 52,
          align: 'center',
          x: 105,
          y: 115,
          fill: '#d63384',
        },
      },
      {
        id: 'great-vibes',
        label: 'With love',
        previewScale: 1.6,
        style: {
          text: 'With love',
          fontFamily: 'GreatVibes',
          fontSize: 52,
          width: 280,
          height: 58,
          align: 'center',
          x: 120,
          y: 110,
          fill: '#c2185b',
        },
      },
      {
        id: 'pacifico-casual',
        label: 'Casual script',
        previewScale: 1.7,
        style: {
          text: 'Casual script',
          fontFamily: 'Pacifico',
          fontSize: 40,
          width: 300,
          height: 50,
          align: 'center',
          x: 108,
          y: 118,
          fill: '#0d6efd',
        },
      },
      {
        id: 'satisfy-tagline',
        label: 'Sweet tagline',
        previewScale: 1.65,
        style: {
          text: 'Sweet tagline',
          fontFamily: 'Satisfy',
          fontSize: 42,
          width: 290,
          height: 52,
          align: 'center',
          x: 112,
          y: 116,
          fill: '#6f42c1',
        },
      },
      {
        id: 'parisienne-invite',
        label: 'You are invited',
        previewScale: 1.55,
        style: {
          text: 'You are invited',
          fontFamily: 'Parisienne',
          fontSize: 38,
          width: 310,
          height: 48,
          align: 'center',
          x: 100,
          y: 120,
          fill: '#198754',
        },
      },
    ],
  },
  {
    id: 'neon-glow',
    title: 'Neon & glow',
    description: 'High-contrast effects for dark backgrounds',
    templates: [
      {
        id: 'wayfarer-heading',
        label: 'HEADING',
        previewScale: 2,
        style: {
          text: 'HEADING',
          fontFamily: 'Wayfarer',
          fontSize: 50,
          width: 360,
          height: 52,
          align: 'center',
          fontStyle: 'bold',
          letterSpacing: 3,
          x: 95,
          y: 51,
          fill: '#ffffff',
        },
      },
      {
        id: 'wayfarer-welcome',
        label: 'WELCOME',
        previewScale: 2,
        style: {
          text: 'Welcome',
          fontFamily: 'Wayfarer',
          fontSize: 50,
          width: 305,
          height: 55,
          align: 'center',
          fontStyle: 'bold',
          x: 110,
          y: 155,
          fill: 'rgb(208, 79, 248)',
          stroke: 'rgb(191, 181, 90)',
          strokeWidth: 1,
          shadowOffsetX: -4,
          shadowOffsetY: -4,
          shadowBlur: 4,
          shadowColor: 'rgb(240, 255, 245)',
          shadowOpacity: 1,
        },
      },
      {
        id: 'thunder-respark',
        label: 'RESPARK',
        previewScale: 2.2,
        style: {
          text: 'RESPARK',
          fontFamily: 'Thunder',
          fontSize: 60,
          width: 375,
          height: 120,
          align: 'center',
          fontStyle: 'bold',
          letterSpacing: 2,
          lineHeight: 2,
          x: 85,
          y: 117,
          fill: 'rgb(248, 114, 230)',
          stroke: 'rgb(254, 6, 237)',
          strokeWidth: 1,
          shadowOffsetX: -2,
          shadowOffsetY: 0,
          shadowBlur: 44,
          shadowColor: 'rgb(255, 41, 241)',
          shadowOpacity: 1,
        },
      },
      {
        id: 'wayfarer-welcome-stack',
        label: 'Welcome To Respark',
        previewScale: 2,
        style: {
          text: 'Welcome To Respark',
          fontFamily: 'Wayfarer',
          fontSize: 50,
          width: 300,
          height: 160,
          align: 'center',
          fontStyle: 'bold',
          x: 130,
          y: 110,
          fill: '#ffffff',
          stroke: 'rgb(255, 164, 128)',
          strokeWidth: 1,
          shadowOffsetX: -4,
          shadowOffsetY: -4,
          shadowBlur: 4,
          shadowColor: 'rgb(240, 255, 245)',
          shadowOpacity: 1,
        },
      },
      {
        id: 'neon-cyan',
        label: 'NEON NIGHT',
        previewScale: 2.1,
        style: {
          text: 'NEON NIGHT',
          fontFamily: 'Montserrat',
          fontSize: 48,
          width: 380,
          height: 58,
          align: 'center',
          fontStyle: 'bold',
          letterSpacing: 4,
          x: 80,
          y: 100,
          fill: '#00f5ff',
          shadowOffsetX: 0,
          shadowOffsetY: 0,
          shadowBlur: 18,
          shadowColor: '#00f5ff',
          shadowOpacity: 0.9,
        },
      },
      {
        id: 'neon-pink',
        label: 'GLOW UP',
        previewScale: 2.1,
        style: {
          text: 'GLOW UP',
          fontFamily: 'Outfit',
          fontSize: 46,
          width: 360,
          height: 56,
          align: 'center',
          fontStyle: 'bold',
          x: 88,
          y: 102,
          fill: '#ff4fd8',
          shadowOffsetX: 0,
          shadowOffsetY: 0,
          shadowBlur: 22,
          shadowColor: '#ff4fd8',
          shadowOpacity: 0.85,
        },
      },
    ],
  },
  {
    id: 'outline-stroke',
    title: 'Outline & stroke',
    description: 'Readable on busy photos',
    templates: [
      {
        id: 'stroke-white-fill',
        label: 'OUTLINED',
        previewScale: 2,
        style: {
          text: 'OUTLINED',
          fontFamily: 'Montserrat',
          fontSize: 52,
          width: 360,
          height: 62,
          align: 'center',
          fontStyle: 'bold',
          x: 90,
          y: 100,
          fill: '#ffffff',
          stroke: '#111111',
          strokeWidth: 2,
        },
      },
      {
        id: 'stroke-brand',
        label: 'Brand stroke',
        previewScale: 1.85,
        style: {
          text: 'Brand stroke',
          fontFamily: 'Poppins',
          fontSize: 44,
          width: 340,
          height: 54,
          align: 'center',
          fontStyle: 'bold',
          x: 95,
          y: 108,
          fill: '#f8558e',
          stroke: '#ffffff',
          strokeWidth: 2,
        },
      },
      {
        id: 'league-spartan-outline',
        label: 'BLOCK OUTLINE',
        previewScale: 2.15,
        themeAdaptiveFill: true,
        style: {
          text: 'BLOCK OUTLINE',
          fontFamily: 'League Spartan',
          fontSize: 50,
          width: 400,
          height: 60,
          align: 'center',
          fontStyle: 'bold',
          letterSpacing: 2,
          x: 70,
          y: 98,
          stroke: '#f8558e',
          strokeWidth: 2,
        },
      },
    ],
  },
  {
    id: 'promo-sale',
    title: 'Promo & badges',
    description: 'Sales, launches, and callouts',
    templates: [
      {
        id: 'sale-badge',
        label: '🔥 SALE',
        previewScale: 2.3,
        style: {
          text: 'SALE',
          fontFamily: 'Bebas Neue',
          fontSize: 64,
          width: 200,
          height: 70,
          align: 'center',
          x: 150,
          y: 100,
          fill: '#ffffff',
          stroke: '#e63946',
          strokeWidth: 0,
          letterSpacing: 4,
        },
        keywords: ['discount', 'offer'],
      },
      {
        id: 'new-badge',
        label: '✨ NEW',
        previewScale: 2.4,
        style: {
          text: 'NEW',
          fontFamily: 'Anton',
          fontSize: 56,
          width: 180,
          height: 62,
          align: 'center',
          x: 160,
          y: 105,
          fill: '#22c55e',
        },
      },
      {
        id: 'percent-off',
        label: '50% OFF',
        previewScale: 2,
        style: {
          text: '50% OFF',
          fontFamily: 'Archivo Black',
          fontSize: 48,
          width: 320,
          height: 58,
          align: 'center',
          x: 100,
          y: 108,
          fill: '#fbbf24',
          stroke: '#111111',
          strokeWidth: 1,
        },
      },
      {
        id: 'limited-time',
        label: 'Limited time',
        previewScale: 1.7,
        themeAdaptiveFill: true,
        style: {
          text: 'Limited time offer',
          fontFamily: 'Roboto Condensed',
          fontSize: 28,
          width: 340,
          height: 40,
          align: 'center',
          fontStyle: 'bold',
          letterSpacing: 1,
          x: 90,
          y: 118,
        },
      },
      {
        id: 'shop-now',
        label: 'SHOP NOW',
        previewScale: 2.1,
        style: {
          text: 'SHOP NOW',
          fontFamily: 'Barlow Condensed',
          fontSize: 50,
          width: 300,
          height: 56,
          align: 'center',
          fontStyle: 'bold',
          letterSpacing: 3,
          x: 110,
          y: 104,
          fill: '#ffffff',
        },
        keywords: ['cta', 'button'],
      },
      marketingBanner('bogo', 'BUY 1 GET 1 FREE', 'Bebas Neue', 52, 400, {
        fill: '#ffffff',
        letterSpacing: 2,
      }),
      marketingBanner('flash-sale', 'FLASH SALE', 'Anton', 62, 360, {
        fill: '#ef4444',
        letterSpacing: 2,
      }),
      marketingBanner('free-shipping', 'FREE SHIPPING', 'Barlow Condensed', 44, 380, {
        fontStyle: 'bold',
        fill: '#22c55e',
        letterSpacing: 2,
      }),
    ],
  },
  {
    id: 'offers-discounts',
    title: 'Offers & discounts',
    description: 'Percent-off, codes, urgency, and seasonal deal lines',
    templates: [
      marketingBanner('off-10', '10% OFF', 'Bebas Neue', 58, 260, {
        fill: '#ef4444',
        letterSpacing: 2,
        keywords: ['discount', 'percent'],
      }),
      marketingBanner('off-20', '20% OFF', 'Anton', 56, 280, {
        fill: '#f97316',
        keywords: ['discount', 'percent'],
      }),
      marketingBanner('off-30', '30% OFF', 'Archivo Black', 54, 300, {
        fill: '#fbbf24',
        stroke: '#111111',
        strokeWidth: 1,
        keywords: ['discount', 'percent'],
      }),
      marketingBanner('off-40', '40% OFF', 'Bebas Neue', 56, 280, {
        fill: '#ffffff',
        stroke: '#dc2626',
        strokeWidth: 2,
        keywords: ['discount', 'percent'],
      }),
      marketingBanner('off-50', '50% OFF', 'Lilita One', 52, 280, {
        fill: '#f8558e',
        keywords: ['discount', 'percent', 'half'],
      }),
      marketingBanner('extra-off', 'EXTRA 15% OFF', 'Barlow Condensed', 44, 360, {
        fontStyle: 'bold',
        fill: '#7c3aed',
        letterSpacing: 2,
        keywords: ['stacked offer'],
      }),
      marketingBanner('save-amount', 'SAVE $50', 'Oswald', 52, 280, {
        fill: '#16a34a',
        letterSpacing: 2,
        keywords: ['dollar', 'deal'],
      }),
      marketingBanner('promo-code', 'CODE: SAVE10', 'Roboto Condensed', 32, 340, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        letterSpacing: 2,
        previewScale: 1.75,
        keywords: ['coupon', 'voucher'],
      }),
      marketingBanner('take-off', 'TAKE 25% OFF', 'League Spartan', 46, 360, {
        fontStyle: 'bold',
        fill: '#0ea5e9',
        letterSpacing: 2,
      }),
      marketingBanner('lowest-price', 'LOWEST PRICE', 'Montserrat', 40, 360, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        letterSpacing: 2,
      }),
      marketingBanner('price-drop', 'PRICE DROP', 'Bebas Neue', 54, 320, {
        fill: '#ef4444',
        letterSpacing: 3,
      }),
      marketingBanner('today-only', 'TODAY ONLY', 'Anton', 50, 320, {
        fill: '#ffffff',
        letterSpacing: 2,
        keywords: ['urgency'],
      }),
      marketingBanner('ends-tonight', 'ENDS TONIGHT', 'Archivo Black', 48, 340, {
        fill: '#fbbf24',
        keywords: ['urgency', 'countdown'],
      }),
      marketingBanner('weekend-deal', 'WEEKEND DEAL', 'Outfit', 42, 360, {
        fontStyle: 'bold',
        fill: '#6366f1',
        letterSpacing: 2,
      }),
      marketingBanner('cyber-deal', 'CYBER DEAL', 'Bebas Neue', 56, 320, {
        fill: '#06b6d4',
        letterSpacing: 2,
        keywords: ['seasonal'],
      }),
      marketingBanner('black-friday', 'BLACK FRIDAY', 'Anton', 52, 380, {
        fill: '#ffffff',
        letterSpacing: 2,
        keywords: ['seasonal'],
      }),
      marketingBanner('outlet-price', 'OUTLET PRICE', 'Saira Condensed', 44, 360, {
        fontStyle: 'bold',
        fill: '#ea580c',
        letterSpacing: 2,
      }),
      marketingBanner('doorbuster', 'DOORBUSTER', 'Bebas Neue', 54, 340, {
        fill: '#dc2626',
        letterSpacing: 2,
        keywords: ['retail'],
      }),
    ],
  },
  {
    id: 'new-arrivals',
    title: 'New arrivals',
    description: 'Launch, drop, and restock callouts for product banners',
    templates: [
      marketingBanner('new-arrival', 'NEW ARRIVAL', 'Bebas Neue', 56, 360, {
        fill: '#22c55e',
        letterSpacing: 3,
        keywords: ['launch', 'fresh'],
      }),
      marketingBanner('new-arrivals-plural', 'NEW ARRIVALS', 'Anton', 54, 380, {
        fill: '#22c55e',
        letterSpacing: 2,
      }),
      marketingBanner('just-dropped', 'JUST DROPPED', 'Archivo Black', 50, 360, {
        fill: '#f8558e',
        letterSpacing: 2,
        keywords: ['drop', 'hype'],
      }),
      marketingBanner('fresh-in', 'FRESH IN', 'Oswald', 52, 280, {
        letterSpacing: 4,
        themeAdaptiveFill: true,
      }),
      marketingBanner('now-in-stock', 'NOW IN STOCK', 'Barlow Condensed', 44, 340, {
        fontStyle: 'bold',
        fill: '#14b8a6',
        letterSpacing: 2,
      }),
      marketingBanner('restocked', 'RESTOCKED', 'Montserrat', 46, 320, {
        fontStyle: 'bold',
        fill: '#0ea5e9',
        letterSpacing: 2,
      }),
      marketingBanner('back-in-stock', 'BACK IN STOCK', 'Roboto Condensed', 38, 380, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        letterSpacing: 1,
        previewScale: 1.85,
      }),
      marketingBanner('latest-drop', 'LATEST DROP', 'League Spartan', 48, 340, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        letterSpacing: 3,
      }),
      marketingBanner('introducing', 'INTRODUCING', 'Playfair Display', 40, 360, {
        fontStyle: 'italic',
        themeAdaptiveFill: true,
        letterSpacing: 4,
        previewScale: 1.8,
      }),
      marketingBanner('season-new', 'NEW SEASON', 'Bebas Neue', 54, 300, {
        fill: '#a855f7',
        letterSpacing: 3,
      }),
      marketingBanner('pre-order', 'PRE-ORDER NOW', 'FiraSansCondensed', 40, 380, {
        fontStyle: 'bold',
        fill: '#6366f1',
        letterSpacing: 1,
        previewScale: 1.85,
      }),
      marketingBanner('coming-soon-arrival', 'COMING SOON', 'Anton', 50, 340, {
        themeAdaptiveFill: true,
        letterSpacing: 2,
        keywords: ['teaser'],
      }),
      marketingBanner('just-launched', 'JUST LAUNCHED', 'Plus Jakarta Sans', 38, 360, {
        fontStyle: 'bold',
        fill: '#f8558e',
        letterSpacing: 1,
        previewScale: 1.85,
      }),
      marketingBanner('shop-new', 'SHOP NEW', 'Bebas Neue', 56, 280, {
        fill: '#111111',
        letterSpacing: 3,
        keywords: ['cta'],
      }),
    ],
  },
  {
    id: 'tags-labels',
    title: 'Tags & labels',
    description: 'Small badges for corners, stickers, and product highlights',
    templates: [
      retailTag('tag-hot', 'HOT', 'Anton', 36, 120, { fill: '#ef4444', keywords: ['sticker'] }),
      retailTag('tag-sale-sm', 'SALE', 'Bebas Neue', 32, 110, { fill: '#ffffff' }),
      retailTag('tag-new-sm', 'NEW', 'Anton', 30, 100, { fill: '#22c55e' }),
      retailTag('tag-best-seller', 'BEST SELLER', 'Barlow Condensed', 18, 200, {
        fill: '#fbbf24',
        keywords: ['bestseller'],
      }),
      retailTag('tag-top-rated', 'TOP RATED', 'Roboto Condensed', 16, 180, {
        themeAdaptiveFill: true,
        previewScale: 2.8,
      }),
      retailTag('tag-trending', 'TRENDING', 'Montserrat', 20, 180, {
        fontStyle: 'bold',
        fill: '#ec4899',
      }),
      retailTag('tag-limited', 'LIMITED', 'League Spartan', 22, 160, {
        fill: '#111111',
        letterSpacing: 3,
      }),
      retailTag('tag-exclusive', 'EXCLUSIVE', 'Archivo Narrow', 18, 200, {
        themeAdaptiveFill: true,
        letterSpacing: 3,
        previewScale: 2.75,
      }),
      retailTag('tag-online-only', 'ONLINE ONLY', 'Roboto Condensed', 16, 200, {
        fill: '#0ea5e9',
        previewScale: 2.8,
      }),
      retailTag('tag-in-store', 'IN STORE', 'Barlow Condensed', 18, 160, {
        themeAdaptiveFill: true,
        previewScale: 2.75,
      }),
      retailTag('tag-sold-out', 'SOLD OUT', 'Anton', 28, 200, {
        fill: '#6b7280',
        keywords: ['status'],
      }),
      retailTag('tag-free-gift', 'FREE GIFT', 'Bebas Neue', 24, 200, {
        fill: '#22c55e',
      }),
      retailTag('tag-best-value', 'BEST VALUE', 'Oswald', 20, 200, {
        fill: '#16a34a',
        previewScale: 2.7,
      }),
      retailTag('tag-staff-pick', 'STAFF PICK', 'Poppins', 16, 180, {
        fontStyle: 'bold',
        fill: '#f8558e',
        previewScale: 2.8,
      }),
      retailTag('tag-organic', 'ORGANIC', 'Montserrat', 18, 160, {
        fill: '#15803d',
        letterSpacing: 2,
        keywords: ['eco'],
      }),
      retailTag('tag-vegan', 'VEGAN', 'Outfit', 20, 140, {
        fontStyle: 'bold',
        fill: '#65a30d',
        keywords: ['eco'],
      }),
      retailTag('tag-handmade', 'HANDMADE', 'Cormorant Garamond', 20, 200, {
        fontStyle: 'italic',
        themeAdaptiveFill: true,
        previewScale: 2.7,
      }),
      retailTag('tag-limited-stock', 'LIMITED STOCK', 'Roboto Condensed', 16, 220, {
        fill: '#ef4444',
        previewScale: 2.85,
        keywords: ['urgency'],
      }),
      retailTag('tag-clearance-sm', 'CLEARANCE', 'Bebas Neue', 26, 200, {
        fill: '#dc2626',
      }),
      retailTag('tag-offer-sm', 'OFFER', 'Archivo Black', 28, 140, {
        fill: '#fbbf24',
        keywords: ['discount'],
      }),
    ],
  },
  {
    id: 'brand-identity',
    title: 'Brand & identity',
    description: 'Logos lockups, taglines, and trust lines for banners',
    templates: [
      marketingBanner('brand-name-hero', 'YOUR BRAND', 'Bebas Neue', 64, 420, {
        fill: '#f8558e',
        letterSpacing: 3,
        keywords: ['logo', 'company'],
      }),
      marketingBanner('brand-tagline', 'Your tagline goes here', 'Montserrat', 28, 400, {
        themeAdaptiveFill: true,
        fontStyle: 'normal',
        previewScale: 1.65,
        keywords: ['slogan', 'subtitle'],
      }),
      marketingBanner('brand-promise', 'Crafted with care', 'Playfair Display', 36, 360, {
        themeAdaptiveFill: true,
        fontStyle: 'italic',
        previewScale: 1.75,
      }),
      marketingBanner('brand-premium', 'PREMIUM QUALITY', 'DM Serif Display', 40, 380, {
        themeAdaptiveFill: true,
        letterSpacing: 4,
      }),
      marketingBanner('brand-trust', 'Trusted by thousands', 'Inter', 24, 340, {
        themeAdaptiveFill: true,
        previewScale: 1.6,
        keywords: ['social proof'],
      }),
      marketingBanner('brand-established', 'EST. 2020', 'Roboto Condensed', 22, 200, {
        themeAdaptiveFill: true,
        letterSpacing: 3,
        previewScale: 1.55,
      }),
      marketingBanner('brand-website', 'www.yourbrand.com', 'Barlow Condensed', 20, 320, {
        themeAdaptiveFill: true,
        letterSpacing: 1,
        previewScale: 1.5,
        keywords: ['url', 'link'],
      }),
      marketingBanner('brand-hashtag', '#YourBrand', 'Poppins', 26, 260, {
        fontStyle: 'bold',
        fill: '#f8558e',
        previewScale: 1.65,
        keywords: ['social'],
      }),
      marketingBanner('brand-mission', 'Inspired to inspire', 'Cormorant Garamond', 32, 360, {
        fontStyle: 'italic',
        fill: '#334155',
        previewScale: 1.7,
      }),
      marketingBanner('brand-partner', 'IN PARTNERSHIP WITH', 'Archivo Narrow', 18, 360, {
        themeAdaptiveFill: true,
        letterSpacing: 4,
        fontStyle: 'bold',
        previewScale: 1.5,
      }),
    ],
  },
  {
    id: 'marketing-banners',
    title: 'Marketing & banners',
    description: 'Campaign headlines, offers, and CTAs for ads and web banners',
    templates: [
      marketingBanner('banner-campaign', 'SUMMER COLLECTION', 'Oswald', 58, 440, {
        themeAdaptiveFill: true,
        letterSpacing: 4,
        keywords: ['seasonal', 'launch'],
      }),
      marketingBanner('banner-up-to-off', 'UP TO 70% OFF', 'Bebas Neue', 60, 400, {
        fill: '#fbbf24',
        stroke: '#111111',
        strokeWidth: 1,
        letterSpacing: 2,
      }),
      marketingBanner('banner-grand-opening', 'GRAND OPENING', 'Anton', 56, 400, {
        fill: '#ffffff',
        stroke: '#f8558e',
        strokeWidth: 2,
      }),
      marketingBanner('banner-last-chance', 'LAST CHANCE', 'Archivo Black', 54, 380, {
        fill: '#ef4444',
        letterSpacing: 2,
      }),
      marketingBanner('banner-members', 'MEMBERS ONLY', 'League Spartan', 48, 380, {
        fontStyle: 'bold',
        fill: '#6366f1',
        letterSpacing: 3,
      }),
      marketingBanner('banner-event', 'LIVE EVENT 2026', 'Barlow Condensed', 50, 400, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        letterSpacing: 2,
      }),
      marketingBanner('banner-order-now', 'ORDER NOW', 'Bebas Neue', 56, 320, {
        fill: '#ffffff',
        letterSpacing: 3,
        keywords: ['cta'],
      }),
      marketingBanner('banner-learn-more', 'LEARN MORE', 'Montserrat', 42, 300, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        letterSpacing: 2,
        keywords: ['cta'],
      }),
      marketingBanner('banner-sign-up', 'SIGN UP TODAY', 'Outfit', 38, 360, {
        fontStyle: 'bold',
        fill: '#0ea5e9',
        letterSpacing: 1,
        keywords: ['cta', 'lead'],
      }),
      marketingBanner('banner-book-now', 'BOOK NOW', 'FiraSansCondensed', 48, 280, {
        fontStyle: 'bold',
        fill: '#14b8a6',
        keywords: ['cta', 'appointment'],
      }),
      marketingBanner('banner-giveaway', 'ENTER TO WIN', 'Lilita One', 46, 360, {
        fill: '#a855f7',
      }),
      marketingBanner('banner-bundle', 'BUNDLE & SAVE', 'Saira Condensed', 44, 360, {
        fontStyle: 'bold',
        fill: '#f97316',
        letterSpacing: 2,
      }),
      marketingBanner('banner-launch', 'NOW AVAILABLE', 'Plus Jakarta Sans', 40, 380, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        letterSpacing: 2,
      }),
      marketingBanner('banner-holiday', 'HOLIDAY SALE', 'Bebas Neue', 58, 360, {
        fill: '#dc2626',
        letterSpacing: 2,
      }),
      marketingBanner('banner-clearance', 'CLEARANCE', 'Anton', 60, 340, {
        fill: '#ffffff',
        letterSpacing: 3,
      }),
      marketingBanner('banner-subhead-offer', 'Ends Sunday · Online only', 'Roboto Condensed', 22, 400, {
        themeAdaptiveFill: true,
        previewScale: 1.55,
        keywords: ['urgency', 'fine print'],
      }),
      marketingBanner('banner-headline-stack', 'NEW ARRIVALS\nSHOP THE DROP', 'Montserrat', 32, 380, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        lineHeight: 1.15,
        height: 80,
        previewScale: 1.5,
        keywords: ['stacked', 'multiline'],
      }),
    ],
  },
  {
    id: 'social-digital',
    title: 'Social & digital',
    description: 'Short hooks for stories, posts, and performance ads',
    templates: [
      marketingBanner('social-follow', 'FOLLOW US', 'Barlow Condensed', 40, 280, {
        fontStyle: 'bold',
        fill: '#f8558e',
        keywords: ['instagram', 'social'],
      }),
      marketingBanner('social-swipe', 'SWIPE UP', 'Montserrat', 36, 260, {
        fontStyle: 'bold',
        fill: '#ffffff',
        letterSpacing: 2,
        keywords: ['story'],
      }),
      marketingBanner('social-link-bio', 'LINK IN BIO', 'Outfit', 32, 300, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        letterSpacing: 1,
      }),
      marketingBanner('social-handle', '@yourbrand', 'Poppins', 28, 240, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        previewScale: 1.65,
      }),
      marketingBanner('social-new-post', 'NEW POST', 'Anton', 44, 260, {
        fill: '#22c55e',
      }),
      marketingBanner('social-limited-drop', 'LIMITED DROP', 'Bebas Neue', 48, 340, {
        fill: '#111111',
        letterSpacing: 2,
        keywords: ['drop', 'hype'],
      }),
      marketingBanner('social-ad-headline', 'Results you can see', 'Inter', 30, 380, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        previewScale: 1.7,
        keywords: ['performance', 'meta', 'google ads'],
      }),
      marketingBanner('social-cta-tap', 'TAP TO SHOP', 'Archivo Black', 38, 300, {
        fill: '#f8558e',
        keywords: ['cta', 'mobile'],
      }),
      marketingBanner('social-download', 'DOWNLOAD THE APP', 'Roboto Condensed', 32, 400, {
        fontStyle: 'bold',
        themeAdaptiveFill: true,
        letterSpacing: 1,
        previewScale: 1.75,
      }),
      marketingBanner('social-subscribe', 'SUBSCRIBE', 'League Spartan', 42, 280, {
        fontStyle: 'bold',
        fill: '#ef4444',
        letterSpacing: 3,
        keywords: ['youtube', 'newsletter'],
      }),
    ],
  },
];

const coreQuickCategory = CORE_TEXT_TEMPLATE_CATEGORIES.find((c) => c.id === 'quick');
const coreWithoutQuick = CORE_TEXT_TEMPLATE_CATEGORIES.filter((c) => c.id !== 'quick');

export const TEXT_TEMPLATE_CATEGORIES: TextTemplateCategory[] = [
  ...(coreQuickCategory ? [coreQuickCategory] : []),
  ...SALON_SPA_CLINIC_TEXT_TEMPLATE_CATEGORIES,
  ...coreWithoutQuick,
  ...EXTRA_TEXT_TEMPLATE_CATEGORIES,
];

export const ALL_TEXT_TEMPLATES: TextTemplateItem[] = TEXT_TEMPLATE_CATEGORIES.flatMap(
  (cat) => cat.templates,
);

/** @deprecated Use TEXT_TEMPLATE_CATEGORIES — kept for imports from `textStyles.js`. */
export const Headings = TEXT_TEMPLATE_CATEGORIES.find((c) => c.id === 'quick')!.templates.map(
  (t) => applyThemeToTemplateStyle('light', t),
);

/** @deprecated Decorative presets — neon category + legacy consumers. */
const LEGACY_DECORATIVE_IDS = new Set([
  'wayfarer-heading',
  'wayfarer-welcome',
  'thunder-respark',
  'wayfarer-welcome-stack',
]);

export const legacyDecorativeTextStyles = ALL_TEXT_TEMPLATES.filter((t) =>
  LEGACY_DECORATIVE_IDS.has(t.id),
).map((t) => t.style);

export default legacyDecorativeTextStyles;
